import clsx from 'clsx'
import { EyeIcon, EyeOffIcon, TrashIcon, Undo2Icon } from 'lucide-react'
import React, { useEffect, useState } from 'react'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { type AdditionalParticipant } from 'components/member_onboarding_v2/types'
import { renderFormattedSSN } from 'components/members/utils'
import { Banner, Button, IconButton, TextButton } from 'slab'
import { capitalize, getFormattedDate, snakeToSentenceCase } from 'utils'
import {
  usePostMemberOnboardingEnrollmentEventsEnrollmentEventIdAdditionalParticipantsParticipantIdMarkAsRemoved as useMarkAsRemovedMutation,
  usePostMemberOnboardingEnrollmentEventsEnrollmentEventIdAdditionalParticipantsParticipantIdUndoRemove as useUndoRemoveMutation,
} from 'utils/web_api'

import { useOnboarding } from '../../OnboardingContext'
import { hasExistingOnThatchEnrollments } from '../../utils'
import { OnboardingGenericErrorMessage } from '../OnboardingGenericErrorMessage'

interface RenewalsCoveredDependentProps {
  coveredParticipant: AdditionalParticipant;
  enrollmentEventId: string;
  planYear?: number;
  handleRefresh: () => void;
  onTriggerModal: (action: () => void) => void;
  modalSubmitted: boolean;
  modalClosed: boolean;
  hasExistingSpouse: boolean;
  setDisableNext: (disableNext: boolean) => void;
}

const LABEL_CONTAINER_CLASSES =
  'flex items-center gap-1 body-sm text-gray-500 mb-1 h-5'

export const constructIneligibleMessage = ({
  coveredParticipant,
  reason,
}: {
  coveredParticipant: AdditionalParticipant;
  reason?: string;
}) => {
  if (reason === 'age_over_25') {
    const cpGender = coveredParticipant.gender
    let genderText = ''

    if (cpGender === 'male') {
      genderText = 'he'
    } else if (cpGender === 'female') {
      genderText = 'she'
    } else {
      // non_binary case
      genderText = 'they'
    }
    return `Since ${coveredParticipant.given_name} turns 26, ${genderText} can no longer be covered on your insurance plans.`
  }
  return `${coveredParticipant.given_name} can no longer be covered on your insurance plans. Please contact support if you think this is a mistake.`
}

const RenewalsCoveredDependent: React.FC<RenewalsCoveredDependentProps> = ({
  coveredParticipant,
  enrollmentEventId,
  planYear,
  handleRefresh,
  onTriggerModal,
  setDisableNext,
  modalSubmitted,
  modalClosed: _modalClosed,
  hasExistingSpouse,
}) => {
  const [showFullSSN, setShowFullSSN] = useState(false)
  const [submitError, setSubmitError] = useState<React.ReactElement | null>(
    null
  )
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null)

  const { deleteAdditionalParticipant, onboardingData } = useOnboarding()
  const enrollmentsPresent = hasExistingOnThatchEnrollments(onboardingData)

  // Handle modal submission
  useEffect(() => {
    if (modalSubmitted && pendingAction) {
      pendingAction()
      setPendingAction(null)
    }
  }, [modalSubmitted, pendingAction])

  const cpWillBeRemoved =
    coveredParticipant.selected_plan_status === 'will_be_removed'
  const cpIsIneligible =
    coveredParticipant.selected_plan_status === 'not_eligible'
  const cpIsIneligibleMessage = cpIsIneligible
    ? constructIneligibleMessage({
      coveredParticipant,
      reason: coveredParticipant.selected_plan_status_ineligible_reason,
    })
    : null

  const mutationOptions = {
    mutation: {
      onSuccess: () => {
        handleRefresh()
        setDisableNext(false)
      },
      onError: (error: GrapeAPIErrorType) => {
        setSubmitError(<OnboardingGenericErrorMessage error={error} />)
      },
    },
  }
  const markAsRemovedMutation = useMarkAsRemovedMutation(mutationOptions)
  const undoRemoveMutation = useUndoRemoveMutation(mutationOptions)
  const removeDependentMutation = deleteAdditionalParticipant(
    coveredParticipant.id
  )

  const toggleSSNVisibility = () => {
    setShowFullSSN(!showFullSSN)
  }

  const handleRemoveClick = () => {
    const action = () => {
      if (coveredParticipant.selected_plan_status === 'ported_over') {
        setDisableNext(true)
        markAsRemovedMutation.mutate({
          enrollmentEventId,
          participantId: coveredParticipant.id,
        })
      } else if (coveredParticipant.selected_plan_status === 'will_be_added') {
        setDisableNext(true)
        removeDependentMutation.mutate(undefined, mutationOptions.mutation)
      }
    }

    if (enrollmentsPresent) {
      setPendingAction(() => action)
      onTriggerModal(action)
    } else {
      action()
    }
  }

  const handleUndoRemoveClick = () => {
    const action = () => {
      setDisableNext(false)
      undoRemoveMutation.mutate({
        enrollmentEventId,
        participantId: coveredParticipant.id,
      })
    }

    if (enrollmentsPresent) {
      setPendingAction(() => action)
      onTriggerModal(action)
    } else {
      action()
    }
  }

  return (
    <div className='border border-gray-200 rounded-lg p-3 shadow-sm bg-thatch-50 gap-y-3 flex flex-col'>
      <div className='flex justify-between items-center gap-4'>
        <div className='flex items-center flex-wrap gap-2'>
          <h3
            className={clsx(
              'body-bold sensitive',
              (cpWillBeRemoved || cpIsIneligible) &&
                'line-through text-gray-500'
            )}
          >
            {coveredParticipant.given_name} {coveredParticipant.family_name}
          </h3>
          <span
            className={clsx(
              'body text-gray-500',
              (cpWillBeRemoved || cpIsIneligible) &&
                'line-through text-gray-500'
            )}
          >
            {capitalize(coveredParticipant.relationship)}
          </span>
        </div>
        {(coveredParticipant.selected_plan_status === 'ported_over' ||
          coveredParticipant.selected_plan_status === 'will_be_added') && (
            <TextButton
              leftIcon={TrashIcon}
              onClick={handleRemoveClick}
              disabled={
              markAsRemovedMutation.isPending ||
              removeDependentMutation.isPending
            }
            >
              Remove
            </TextButton>
        )}
      </div>
      <div className='grid grid-cols-2 xs:grid-cols-3 gap-4'>
        <div>
          <div
            className={clsx(
              LABEL_CONTAINER_CLASSES,
              (cpWillBeRemoved || cpIsIneligible) &&
                'line-through text-gray-500'
            )}
          >
            <span>Date of birth</span>
          </div>
          <div
            className={clsx(
              'body sensitive',
              (cpWillBeRemoved || cpIsIneligible) &&
                'line-through text-gray-500'
            )}
          >
            {getFormattedDate(coveredParticipant.dob + 'T00:00:00')}
          </div>
        </div>

        <div>
          <div
            className={clsx(
              LABEL_CONTAINER_CLASSES,
              cpWillBeRemoved && 'line-through text-gray-500'
            )}
          >
            <span>SSN</span>
            {coveredParticipant.ssn && !cpWillBeRemoved && !cpIsIneligible && (
              <IconButton
                icon={showFullSSN ? EyeOffIcon : EyeIcon}
                label={showFullSSN ? 'Hide SSN' : 'Show SSN'}
                onClick={toggleSSNVisibility}
              />
            )}
          </div>
          {coveredParticipant.ssn
            ? (
              <div
                className={clsx(
                  'body font-mono sensitive',
                  (cpWillBeRemoved || cpIsIneligible) &&
                  'line-through text-gray-500'
                )}
              >
                {renderFormattedSSN(coveredParticipant.ssn, showFullSSN)}
              </div>
              )
            : (
              <div
                className={clsx(
                  'body',
                  (cpWillBeRemoved || cpIsIneligible) &&
                  'line-through text-gray-500'
                )}
              >
                —
              </div>
              )}
        </div>
        <div>
          <div
            className={clsx(
              LABEL_CONTAINER_CLASSES,
              (cpWillBeRemoved || cpIsIneligible) &&
                'line-through text-gray-500'
            )}
          >
            <span>Gender</span>
          </div>
          <div
            className={clsx(
              'body sensitive',
              (cpWillBeRemoved || cpIsIneligible) &&
                'line-through text-gray-500'
            )}
          >
            {snakeToSentenceCase(coveredParticipant.gender ?? '')}
          </div>
        </div>
      </div>
      {cpWillBeRemoved && (
        <div className='body rounded-lg border border-orange-300 bg-orange-50 text-orange-500 p-4 gap-x-2 flex justify-between items-center'>
          {`You removed this dependent — they won't be covered in ${planYear}`}
          <Button
            size='small'
            variant='secondary'
            rightIcon={Undo2Icon}
            onClick={handleUndoRemoveClick}
            disabled={
              (hasExistingSpouse &&
                coveredParticipant.relationship === 'spouse') ||
              undoRemoveMutation.isPending
            }
          >
            Undo
          </Button>
        </div>
      )}
      {cpIsIneligible && (
        <div className='body rounded-lg border border-orange-300 bg-orange-50 text-orange-500 p-4 gap-x-2 flex justify-between items-center'>
          {cpIsIneligibleMessage}
        </div>
      )}
      {submitError && (
        <Banner
          title='A problem has occurred while processing your request'
          type='critical'
        >
          {submitError}
        </Banner>
      )}
    </div>
  )
}

export default RenewalsCoveredDependent
